import React from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { GeoAlt } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import * as intrfce from "../Interfaces";
import { AddressSelection } from "../utils/billingAddressValidation";
import "./BillingAddressValidationModal.scss";

interface BillingAddressValidationModalProps {
  show: boolean;
  address: intrfce.iAddressFormData;
  recommendedAddress: intrfce.iAddressFormData | null;
  selectedOption: AddressSelection;
  isOriginalAddressValid: boolean;
  isStateApplicable: boolean;
  validationFailed?: boolean;
  onSelectionChange: (selection: AddressSelection) => void;
  onEdit: () => void;
  onConfirm: () => void;
}

const normalize = (value?: string): string =>
  value?.trim().toUpperCase() || "";

const AddressBlock: React.FC<{
  address: intrfce.iAddressFormData;
  originalAddress?: intrfce.iAddressFormData;
}> = ({ address, originalAddress }) => {
  const differenceClass = (
    field: keyof intrfce.iAddressFormData
  ): string | undefined =>
    originalAddress &&
    normalize(String(address[field] || "")) !==
      normalize(String(originalAddress[field] || ""))
      ? "address-different"
      : undefined;

  return (
  <span className="billing-address-modal__address">
      <span className={differenceClass("address")}>{address.address}</span>
      {address.line2 && (
        <span className={differenceClass("line2")}>, {address.line2}</span>
      )}
    <br />
      <span className={differenceClass("cityValue")}>
        {address.cityValue}
      </span>
      {address.stateValue && (
        <span className={differenceClass("stateValue")}>
          , {address.stateValue}
        </span>
      )}
      {address.pincode && (
        <span className={differenceClass("pincode")}>
          {" "}
          {address.pincode}
        </span>
      )}
    <br />
      <span className={differenceClass("countryValue")}>
        {address.countryName || address.countryValue}
      </span>
  </span>
  );
};

const BillingAddressValidationModal: React.FC<
  BillingAddressValidationModalProps
> = ({
  show,
  address,
  recommendedAddress,
  selectedOption,
  isOriginalAddressValid,
  isStateApplicable,
  validationFailed = false,
  onSelectionChange,
  onEdit,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const canConfirm =
    (selectedOption === "yourAddress" && isOriginalAddressValid) ||
    (selectedOption === "recommendedAddress" &&
      recommendedAddress !== null);

  return (
    <Modal
      className="address-modal billing-address-modal"
      show={show}
      backdrop="static"
      keyboard={false}
      aria-labelledby="billing-address-modal-title"
    >
      <Modal.Header>
        <Modal.Title id="billing-address-modal-title">
          <GeoAlt className="me-2" aria-hidden="true" />
          {t("CONFIRM_ADDRESS", {
            defaultValue: "Please confirm the address",
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="address-options">
          <fieldset>
            <legend className="visually-hidden">
              {t("BILLING_ADDRESS", { defaultValue: "Billing address" })}
            </legend>
            <div className="radioGroup">
              <Form.Check
                type="radio"
                id="billing-address-entered"
                name="billingAddressOption"
                checked={selectedOption === "yourAddress"}
                onChange={() => onSelectionChange("yourAddress")}
                label={
                  <span className="labelWrapper">
                    <span className="radioLabels">
                      <strong>
                        {t("YOUR_ADDRESS", {
                          defaultValue: "Your address",
                        })}
                      </strong>
                      <AddressBlock address={address} />
                    </span>
                  </span>
                }
              />

              {recommendedAddress && (
                <Form.Check
                  type="radio"
                  id="billing-address-recommended"
                  name="billingAddressOption"
                  checked={selectedOption === "recommendedAddress"}
                  onChange={() => onSelectionChange("recommendedAddress")}
                  label={
                    <span className="labelWrapper">
                      <span className="radioLabels">
                        <strong>
                          {t("RECOMMENDED_ADDRESS", {
                            defaultValue: "Recommended address",
                          })}
                        </strong>
                        <AddressBlock
                          address={recommendedAddress}
                          originalAddress={address}
                        />
                      </span>
                    </span>
                  }
                />
              )}
            </div>
          </fieldset>
        </div>

        {!isOriginalAddressValid && (
          <div className="billing-address-modal__error" role="alert">
            <strong className="billing-address-modal__error-title">
              {t("ADDRESS_VALIDATION_ERROR", {
                defaultValue: "Address Validation Error",
              })}
            </strong>
            <p className="billing-address-modal__error-message">
              {validationFailed
                ? t("ADDRESS_VALIDATION_UNAVAILABLE", {
                    defaultValue:
                      "We could not validate this billing address. Edit the address and try again.",
                  })
                : isStateApplicable
                  ? t("CITY_STATE_ZIP_MISMATCH", {
                      defaultValue:
                        "The city, state, and ZIP code do not match. Please verify your address.",
                    })
                  : t("ADDRESS_DETAILS_MISMATCH", {
                      defaultValue:
                        "The address details do not match. Please verify your address.",
                    })}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="button-container">
          <Button
            variant="outline-success"
            className="btn-green-outline"
            onClick={onEdit}
          >
            {t("UPDATE_ADDRESS_BUTTON", { defaultValue: "Edit address" })}
          </Button>
          {canConfirm && (
            <Button variant="success" className="btn-green" onClick={onConfirm}>
              {t("CONFIRM_ADDRESS_BUTTON", {
                defaultValue: "Confirm address",
              })}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default BillingAddressValidationModal;
